import {
  NIGERIAN_STATES,
  getLgasForState,
  searchLocations,
} from '../data/nigerianLocations';
import {
  calculateHaversineDistanceKm,
  getEstimatedCoordinates,
  normalizeNigerianState,
  ResolvedNigerianLocation,
} from './nigerianLocationResolver';

export interface LocationSuggestion {
  placeId: string;
  formatted: string;
  addressLine1: string;
  addressLine2: string;
  street?: string;
  city?: string;
  county?: string;
  state?: string;
  country: string;
  lat: number;
  lon: number;
  resultType?: string;
  confidence?: number;
  resolvedNigerian: {
    state: string;
    lga: string;
    cityArea: string;
    isLgaConfident: boolean;
    confidenceScore: number;
  };
}

export interface RouteInfo {
  distanceKm: number;
  distanceMeters: number;
  durationMinutes: number;
  durationFormatted: string;
  mode: string;
}

/**
 * Calls JobMatch NG backend to autocomplete address across Nigeria via Geoapify
 */
export async function searchAddressAutocomplete(
  text: string,
  limit = 8
): Promise<LocationSuggestion[]> {
  const query = (text || '').trim();
  if (!query || query.length < 2) return [];

  try {
    const res = await fetch(`/api/location/autocomplete?text=${encodeURIComponent(query)}&limit=${limit}`);
    if (res.ok) {
      const data = await res.json();
      if (data.success && Array.isArray(data.results) && data.results.length > 0) {
        return data.results;
      }
    }
  } catch (err) {
    console.warn('Backend location autocomplete note:', err);
  }

  // Authoritative fallback: Local Nigerian administrative database
  const localMatches = searchLocations(query, limit);
  return localMatches.map((m, idx) => {
    const coords = getEstimatedCoordinates(m.state, m.lga, m.cityArea) || {
      lat: 6.5244,
      lng: 3.3792,
    };
    const displayName = m.type === 'city_area'
      ? `${m.cityArea}, ${m.lga}, ${m.state}`
      : m.type === 'lga'
      ? `${m.lga} LGA, ${m.state}`
      : `${m.state} State`;

    return {
      placeId: `local-${idx}-${m.state}-${m.lga}`,
      formatted: `${displayName}, Nigeria`,
      addressLine1: m.cityArea || m.lga,
      addressLine2: `${m.lga}, ${m.state}, Nigeria`,
      city: m.cityArea || m.lga,
      county: m.lga,
      state: m.state,
      country: 'Nigeria',
      lat: coords.lat,
      lon: coords.lng,
      resultType: m.type,
      confidence: 0.9,
      resolvedNigerian: {
        state: m.state,
        lga: m.lga,
        cityArea: m.cityArea || m.lga,
        isLgaConfident: true,
        confidenceScore: 90,
      },
    };
  });
}

/**
 * Geocodes an address or landmark to coordinates via backend
 */
export async function geocodeAddress(text: string): Promise<LocationSuggestion | null> {
  const query = (text || '').trim();
  if (!query) return null;

  try {
    const res = await fetch(`/api/location/geocode?text=${encodeURIComponent(query)}`);
    if (res.ok) {
      const data = await res.json();
      if (data.success && data.result) {
        return data.result;
      }
    }
  } catch (err) {
    console.warn('Geocoding error:', err);
  }

  // Fallback to local coordinates
  const state = normalizeNigerianState(query) || 'Lagos';
  const lgas = getLgasForState(state);
  const coords = getEstimatedCoordinates(state) || { lat: 6.5244, lng: 3.3792 };

  return {
    placeId: `local-geo-${state}`,
    formatted: `${state}, Nigeria`,
    addressLine1: state,
    addressLine2: 'Nigeria',
    city: lgas[0] || 'Ikeja',
    county: lgas[0] || 'Ikeja',
    state,
    country: 'Nigeria',
    lat: coords.lat,
    lon: coords.lng,
    resolvedNigerian: {
      state,
      lga: lgas[0] || 'Ikeja',
      cityArea: lgas[0] || 'Ikeja',
      isLgaConfident: false,
      confidenceScore: 50,
    },
  };
}

/**
 * Reverse geocodes coordinates (lat, lon) to Nigerian address via backend
 */
export async function reverseGeocodeCoordinates(
  lat: number,
  lon: number
): Promise<LocationSuggestion> {
  try {
    const res = await fetch(`/api/location/reverse?lat=${lat}&lon=${lon}`);
    if (res.ok) {
      const data = await res.json();
      if (data.success && data.result) {
        return data.result;
      }
    }
  } catch (err) {
    console.warn('Reverse geocoding error:', err);
  }

  // Graceful Fallback
  return {
    placeId: `rev-${lat.toFixed(4)}-${lon.toFixed(4)}`,
    formatted: `Location (${lat.toFixed(4)}, ${lon.toFixed(4)}), Nigeria`,
    addressLine1: 'Selected Location',
    addressLine2: 'Nigeria',
    country: 'Nigeria',
    lat,
    lon,
    resolvedNigerian: {
      state: 'Lagos',
      lga: 'Ikeja',
      cityArea: 'Ikeja',
      isLgaConfident: false,
      confidenceScore: 50,
    },
  };
}

/**
 * Requests browser device GPS location with high accuracy
 */
export async function getDeviceCurrentLocation(): Promise<{
  latitude: number;
  longitude: number;
  accuracy: number;
}> {
  if (typeof navigator === 'undefined' || !navigator.geolocation) {
    throw new Error('Geolocation is not supported by your browser or device.');
  }

  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        resolve({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
        });
      },
      (err) => {
        let msg = 'Could not access GPS location.';
        if (err.code === 1) msg = 'Location permission was denied. Please allow location access.';
        else if (err.code === 2) msg = 'Location position is unavailable. Please check device GPS.';
        else if (err.code === 3) msg = 'Location request timed out. Please try again.';
        reject(new Error(msg));
      },
      {
        enableHighAccuracy: true,
        timeout: 12000,
        maximumAge: 30000,
      }
    );
  });
}

/**
 * Calculates road distance and driving travel time via backend Geoapify routing
 */
export async function calculateDrivingRoute(
  fromLat: number,
  fromLon: number,
  toLat: number,
  toLon: number
): Promise<RouteInfo> {
  try {
    const res = await fetch(
      `/api/location/route?fromLat=${fromLat}&fromLon=${fromLon}&toLat=${toLat}&toLon=${toLon}&mode=drive`
    );
    if (res.ok) {
      const data = await res.json();
      if (data.success && data.route) {
        return data.route;
      }
    }
  } catch (err) {
    console.warn('Route calculation error:', err);
  }

  // Fallback calculation using Haversine * 1.35 road factor
  const directKm = calculateHaversineDistanceKm(fromLat, fromLon, toLat, toLon);
  const roadKm = Math.max(0.5, Math.round(directKm * 1.35 * 10) / 10);
  const minutes = Math.max(3, Math.round((roadKm / 28) * 60));

  let durationFormatted = `${minutes} mins`;
  if (minutes >= 60) {
    const hrs = Math.floor(minutes / 60);
    const mins = minutes % 60;
    durationFormatted = `${hrs} hr ${mins} min${mins !== 1 ? 's' : ''}`;
  }

  return {
    distanceKm: roadKm,
    distanceMeters: Math.round(roadKm * 1000),
    durationMinutes: minutes,
    durationFormatted,
    mode: 'drive',
  };
}
